import * as DialogPrimitive from "@radix-ui/react-dialog";
import clsx from "clsx";
import Icon from "../Icon/Icon";
import React, { createContext, useContext, useState } from "react";
import { createPluggable } from "pluggable-react";
import Button from "../Button/Button";
import { OverlayDepthProvider, useOverlayZIndex } from "../OverlayDepth";
import styles from "./Modal.module.scss";
import { withRefProp } from "../../../utils/withRefProp";

// Context
export interface ModalCloseOptions {
  type?: "dismiss" | "success";
}

interface ModalContextType {
  fullScreen: boolean;
  setFullScreen: (fullScreen: boolean) => void;
  toggleFullScreen: () => void;
  close: (options?: ModalCloseOptions) => void;
}

const ModalContext = createContext<ModalContextType | null>(null);

export const useModalContext = () => {
  const context = useContext(ModalContext);
  return context;
};

// Props
export interface ModalProps extends Omit<React.ComponentProps<typeof DialogPrimitive.Root>, "children" | "onOpenChange"> {
  children: React.ReactNode | ((context: ModalContextType) => React.ReactNode | null | undefined);
  defaultFullScreen?: boolean;
  onOpenChange?: (open: boolean, options?: ModalCloseOptions) => void;
}

const DefaultModal = (props: ModalProps) => {
  const { children, defaultFullScreen = false, onOpenChange, ...otherProps } = props;
  const [ fullScreen, setFullScreen ] = useState(defaultFullScreen);
  const toggleFullScreen = () => setFullScreen(!fullScreen);
  const close = (options?: ModalCloseOptions) => {
    if (onOpenChange) {
      onOpenChange(false, options);
    }
  };

  const contextValue = { fullScreen, setFullScreen, toggleFullScreen, close };
  return (
    <DialogPrimitive.Root {...otherProps} onOpenChange={(open) => onOpenChange?.(open)}>
      <ModalContext.Provider value={contextValue}>
        {typeof children === "function" ? children(contextValue) : children}
      </ModalContext.Provider>
    </DialogPrimitive.Root>
  );
};

const Modal = createPluggable<ModalProps>("deci.modal", DefaultModal);

// Trigger
export type ModalTriggerProps = React.ComponentPropsWithoutRef<typeof DialogPrimitive.Trigger>;

const DefaultModalTrigger = (props: ModalTriggerProps) => <DialogPrimitive.Trigger {...props} />;

const ModalTrigger = createPluggable<ModalTriggerProps>("deci.modal.trigger", DefaultModalTrigger);

// Close
export interface ModalCloseProps {
}

const DefaultModalClose = (_props: ModalCloseProps) => (
  <DialogPrimitive.Close asChild>
    <Button icon label="close"><Icon name="close"/></Button>
  </DialogPrimitive.Close>
);

const ModalClose = createPluggable<ModalCloseProps>("deci.modal.close", DefaultModalClose);

// Overlay
export type ModalOverlayProps = React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>;

export interface ModalOverlayPropsWithRef extends ModalOverlayProps {
  ref?: React.Ref<React.ElementRef<typeof DialogPrimitive.Overlay>>;
}

const DefaultModalOverlay = withRefProp(({ className, ref, ...props }: ModalOverlayPropsWithRef) => (
  <DialogPrimitive.Overlay ref={ref} className={clsx(styles.overlay, className)} {...props} />
));

const ModalOverlay = createPluggable<ModalOverlayProps>("deci.modal.overlay", DefaultModalOverlay);

// Content - base unstyled content
export type ModalContentProps = React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content>;

export interface ModalContentPropsWithRef extends ModalContentProps {
  ref?: React.Ref<React.ElementRef<typeof DialogPrimitive.Content>>;
}

const DefaultModalContent = withRefProp(({ className, children, style, ref, ...props }: ModalContentPropsWithRef) => {
  const context = useContext(ModalContext);
  const fullScreen = context?.fullScreen || false;
  const zIndex = useOverlayZIndex({ offset: 1 });

  return (
    <DialogPrimitive.Portal>
      <OverlayDepthProvider>
        <div
          className={styles.container}
          data-deci-modal="true"
          data-fullscreen={fullScreen}
          style={{ zIndex }}
        >
          <ModalOverlay/>
          <DialogPrimitive.Content
            ref={ref}
            className={clsx(styles.content, className)}
            data-fullscreen={fullScreen}
            style={style}
            {...props}
          >
            {children}
          </DialogPrimitive.Content>
        </div>
      </OverlayDepthProvider>
    </DialogPrimitive.Portal>
  );
});

const ModalContent = createPluggable<ModalContentProps>("deci.modal.content", DefaultModalContent);

// Controls (resize/close buttons)
export interface ModalControlsProps extends React.HTMLAttributes<HTMLDivElement> {
  allowResize?: boolean;
}

const DefaultModalControls = (props: ModalControlsProps) => {
  const { allowResize = false, className, children, ...otherProps } = props;
  const context = useContext(ModalContext);
  const fullScreen = context?.fullScreen || false;
  const toggleFullScreen = context?.toggleFullScreen;
  const close = context?.close;

  return (
    <div className={clsx(styles.controls, className)} {...otherProps}>
      {children}
      {allowResize && toggleFullScreen && (
        <Button icon tabIndex={-1} label={fullScreen ? "Shrink" : "Expand"} onClick={toggleFullScreen}>
          {fullScreen ? <Icon name="minimize"/> : <Icon name="maximize"/>}
        </Button>
      )}
      {close && (
        <Button icon tabIndex={-1} label="Close" onClick={() => close()}>
          <Icon name="close"/>
        </Button>
      )}
    </div>
  );
};

const ModalControls = createPluggable<ModalControlsProps>("deci.modal.controls", DefaultModalControls);

export default Object.assign(Modal, {
  Trigger: ModalTrigger,
  Portal: DialogPrimitive.Portal,
  Overlay: ModalOverlay,
  Close: ModalClose,
  Content: ModalContent,
  Controls: ModalControls,
});
